import { useEffect, useState } from "react";
import { FolderArchive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const GroupFlashReports = () => {
  const [avail, setAvail] = useState([]);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [busy, setBusy] = useState(false);
  const { can } = useAuth();
  useEffect(() => {
    api.get("/flash-reports").then((r) => {
      setAvail(r.data);
      if (r.data.length) { setYear(r.data[0].year); setMonth(r.data[0].months[r.data[0].months.length - 1]); }
    }).catch(() => setAvail([]));
  }, []);
  if (!can("export") || !avail.length || !year) return null;
  const cur = avail.find((a) => a.year === year) || avail[0];
  const dl = async () => {
    setBusy(true);
    try {
      await downloadFile(`/flash-reports/zip?year=${year}&month=${month}`, `flash-reports-${year}-${month}.zip`);
      toast.success(`Flash reports ${MONTHS[month - 1]} ${year} downloaded (${cur.properties.length} properties)`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-900 text-white rounded-lg p-5 grain relative overflow-hidden flex flex-wrap items-end justify-between gap-4 rise" data-testid="group-flash-reports">
      <div>
        <p className="text-xs uppercase tracking-wider text-emerald-300 inline-flex items-center gap-1"><FolderArchive size={13} /> Monthly Flash Reports</p>
        <h3 className="font-heading text-lg font-semibold mt-1">One-page PDF per property: actual vs budget vs reforecast + variance commentary</h3>
        <p className="text-xs text-slate-300 mt-1">Tracked properties for {year}: {cur.properties.join(", ")}</p>
        <p className="text-[11px] text-slate-400 mt-1" data-testid="group-flash-hint">Only months with uploaded actuals can be selected. Upload a month's actual P&amp;L under each project's <span className="text-emerald-300">Budget vs Actual</span> step to enable it.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={year} onChange={(e) => { const y = Number(e.target.value); const a = avail.find((x) => x.year === y); setYear(y); setMonth(a.months[a.months.length - 1]); }} data-testid="group-flash-year-select" className="h-9 rounded-md bg-white text-slate-900 px-3 text-xs">
          {avail.map((a) => <option key={a.year} value={a.year}>{a.year}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="group-flash-month-select" className="h-9 rounded-md bg-white text-slate-900 px-3 text-xs">
          {MONTHS.map((m, i) => <option key={m} value={i + 1} disabled={!cur.months.includes(i + 1)}>{m}{cur.months.includes(i + 1) ? "" : " · no actuals"}</option>)}
        </select>
        <Button onClick={dl} disabled={busy} className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="group-flash-download-button">
          <FolderArchive size={14} className="mr-1.5" /> {busy ? "Preparing..." : "Download all (ZIP)"}
        </Button>
      </div>
    </div>
  );
};
